import { tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import {
  Actions,
  createSelector,
  getActionTypeFromInstance,
  NgxsOnInit,
  State,
  StateContext,
  StateToken,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { assertArray } from '@app/core/utilities/array.utilities';
import { isDeepEqual } from '@app/core/utilities/object.utilities';

export type ActionsStateModel = Record<string, any[]>;

const ACTIONS_STATE = new StateToken<ActionsStateModel>('actions');

enum ActionStatus {
  Dispatched = 'DISPATCHED',
  Successful = 'SUCCESSFUL',
  Canceled = 'CANCELED',
  Errored = 'ERRORED',
}

interface ActionContext<T = any> {
  status: ActionStatus;
  action: T;
  error?: Error;
}

@Injectable()
@State<ActionsStateModel>({
  name: ACTIONS_STATE,
  defaults: {},
})
export class ActionsState implements NgxsOnInit {
  static whileAction(actions: any | any[], withArguments?: object): (state: ActionsStateModel) => boolean {
    actions = assertArray(actions);

    return createSelector([ActionsState], (state: ActionsStateModel) =>
      actions.some(
        (action) =>
          assertArray(state[getActionTypeFromInstance(action)]).filter(
            (dispatched) =>
              !withArguments || Object.entries(withArguments).every(([key, value]) => dispatched[key] === value),
          ).length > 0,
      ),
    );
  }

  constructor(private actions$: Actions) {}

  ngxsOnInit({ setState, getState }: StateContext<ActionsStateModel>): void {
    this.actions$
      .pipe(
        tap((ctx: ActionContext | any) => {
          const type = getActionTypeFromInstance(ctx.action);

          if (!type) {
            return;
          }

          let actions = [...assertArray(getState()[type])];

          const action = Object.entries(ctx.action).reduce(
            (filtered, [key, value]) => ({
              ...filtered,
              ...(value == null || typeof value !== 'object' ? { [key]: value } : {}),
            }),
            {},
          );

          if (ctx.status === ActionStatus.Dispatched) {
            actions.push(action);
          } else {
            actions = actions.filter((target) => !isDeepEqual(target, action));
          }

          setState(patch({ [type]: actions }));
        }),
      )
      .subscribe();
  }
}
